class ProductModel extends DeferredMedia {
  constructor() {
    super();
  }

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    const modelViewer = this.querySelector('model-viewer');
    modelViewer?.setAttribute('interaction-prompt', 'none');
    modelViewer?.setAttribute('disable-zoom', '');

    if (this.dataset.disableModelViewerUi !== 'true') {
      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    }

    window.ATSProductModel?.registerModelViewer(this, modelViewer);
  }
}
customElements.define('product-model', ProductModel);

window.ATSProductModel = {
  normalizeAxes(value) {
    const normalized = (value || 'both').toString().toLowerCase();

    if (['both', 'horizontal_only', 'vertical_only', 'locked'].includes(normalized)) {
      return normalized;
    }

    return 'both';
  },

  readNumber(value) {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  },

  getConfig(productModel) {
    return {
      axes: this.normalizeAxes(productModel.dataset.modelRotationAxes),
      yawMin: this.readNumber(productModel.dataset.modelYawMinDeg),
      yawMax: this.readNumber(productModel.dataset.modelYawMaxDeg),
      pitchMin: this.readNumber(productModel.dataset.modelPitchMinDeg),
      pitchMax: this.readNumber(productModel.dataset.modelPitchMaxDeg),
    };
  },

  normalizeRange(min, max) {
    if (min !== null && max !== null && min > max) {
      return { min: max, max: min };
    }

    return { min, max };
  },

  easeInOutSine(progress) {
    return -(Math.cos(Math.PI * progress) - 1) / 2;
  },

  animateOrbit(modelViewer, fromTheta, fromPhi, toTheta, toPhi, radius, duration, onComplete) {
    if (!modelViewer || duration <= 0) {
      if (modelViewer) {
        modelViewer.cameraOrbit = `${toTheta}rad ${toPhi}rad ${radius}`;
      }

      if (typeof onComplete === 'function') {
        onComplete();
      }

      return;
    }

    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = this.easeInOutSine(progress);
      const currentTheta = fromTheta + ((toTheta - fromTheta) * eased);
      const currentPhi = fromPhi + ((toPhi - fromPhi) * eased);

      modelViewer.cameraOrbit = `${currentTheta}rad ${currentPhi}rad ${radius}`;

      if (progress < 1) {
        window.requestAnimationFrame(step);
        return;
      }

      if (typeof onComplete === 'function') {
        onComplete();
      }
    };

    window.requestAnimationFrame(step);
  },

  getOrbitSnapshot(modelViewer) {
    const orbit = typeof modelViewer.getCameraOrbit === 'function' ? modelViewer.getCameraOrbit() : null;

    if (!orbit || typeof orbit.toString !== 'function') {
      return null;
    }

    const orbitParts = orbit.toString().split(' ');

    return {
      theta: Number(orbit.theta),
      phi: Number(orbit.phi),
      radius: orbit.radius && typeof orbit.radius.toString === 'function' ? orbit.radius.toString() : (orbitParts[2] || 'auto'),
      orbitString: orbit.toString(),
      thetaString: orbitParts[0] || 'auto',
      phiString: orbitParts[1] || 'auto',
    };
  },

  buildOrbitConstraint(theta, phi) {
    return `${theta || 'auto'} ${phi || 'auto'} auto`;
  },

  applyConstraints(productModel, modelViewer) {
    if (!productModel || !modelViewer) {
      return;
    }

    // We render our own 3D badge/cue, so suppress the native prompt overlay.
    modelViewer.setAttribute('interaction-prompt', 'none');
    modelViewer.setAttribute('disable-zoom', '');

    const config = this.getConfig(productModel);
    const yawRange = this.normalizeRange(config.yawMin, config.yawMax);
    const pitchRange = this.normalizeRange(config.pitchMin, config.pitchMax);
    const orbit = this.getOrbitSnapshot(modelViewer);

    if (!orbit) {
      return;
    }

    let minTheta = yawRange.min !== null ? `${yawRange.min}deg` : null;
    let maxTheta = yawRange.max !== null ? `${yawRange.max}deg` : null;
    let minPhi = pitchRange.min !== null ? `${pitchRange.min}deg` : null;
    let maxPhi = pitchRange.max !== null ? `${pitchRange.max}deg` : null;

    if (config.axes === 'horizontal_only' || config.axes === 'locked') {
      minPhi = orbit.phiString;
      maxPhi = orbit.phiString;
    }

    if (config.axes === 'vertical_only' || config.axes === 'locked') {
      minTheta = orbit.thetaString;
      maxTheta = orbit.thetaString;
    }

    if (config.axes === 'locked') {
      modelViewer.removeAttribute('camera-controls');
    } else {
      modelViewer.setAttribute('camera-controls', '');
    }

    if (minTheta || minPhi) {
      modelViewer.setAttribute('min-camera-orbit', this.buildOrbitConstraint(minTheta, minPhi));
    } else {
      modelViewer.removeAttribute('min-camera-orbit');
    }

    if (maxTheta || maxPhi) {
      modelViewer.setAttribute('max-camera-orbit', this.buildOrbitConstraint(maxTheta, maxPhi));
    } else {
      modelViewer.removeAttribute('max-camera-orbit');
    }

    productModel.dataset.modelReady = 'true';
    productModel.dispatchEvent(new CustomEvent('product-model:ready', {
      bubbles: true,
      detail: {
        modelViewer,
        productModel,
      },
    }));
  },

  registerModelViewer(productModel, modelViewer) {
    if (!productModel || !modelViewer) {
      return;
    }

    if (productModel.dataset.modelReady === 'true') {
      return;
    }

    const attemptApply = (remainingAttempts = 6) => {
      if (productModel.dataset.modelReady === 'true') {
        return;
      }

      if (this.getOrbitSnapshot(modelViewer)) {
        this.applyConstraints(productModel, modelViewer);
        return;
      }

      if (remainingAttempts > 0) {
        window.setTimeout(() => attemptApply(remainingAttempts - 1), 150);
      }
    };

    if (modelViewer.loaded) {
      attemptApply();
      return;
    }

    modelViewer.addEventListener('load', () => attemptApply(), { once: true });
    window.setTimeout(() => attemptApply(), 500);
  },

  runCue(modelViewer, rotationAxes, onComplete) {
    if (!modelViewer) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    const orbit = this.getOrbitSnapshot(modelViewer);

    if (!orbit) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    const finish = () => {
      if (typeof onComplete === 'function') {
        onComplete();
      }
    };

    if (rotationAxes === 'locked') {
      finish();
      return;
    }

    const radius = orbit.radius || 'auto';
    const steps = [];

    try {
      if (rotationAxes === 'both' || rotationAxes === 'horizontal_only') {
        const yawOffset = 5 * Math.PI / 180;
        steps.push(
          { theta: orbit.theta + yawOffset, phi: orbit.phi, duration: 380 },
          { hold: 120 },
          { theta: orbit.theta - yawOffset, phi: orbit.phi, duration: 520 },
          { hold: 120 },
          { theta: orbit.theta, phi: orbit.phi, duration: 380 }
        );
      } else if (rotationAxes === 'vertical_only') {
        const pitchOffset = 4 * Math.PI / 180;
        steps.push(
          { theta: orbit.theta, phi: orbit.phi - pitchOffset, duration: 380 },
          { hold: 120 },
          { theta: orbit.theta, phi: orbit.phi + pitchOffset, duration: 520 },
          { hold: 120 },
          { theta: orbit.theta, phi: orbit.phi, duration: 380 }
        );
      }

      if (!steps.length) {
        finish();
        return;
      }

      let currentTheta = orbit.theta;
      let currentPhi = orbit.phi;

      const runStep = (index) => {
        const step = steps[index];

        if (!step) {
          finish();
          return;
        }

        if (step.hold) {
          window.setTimeout(() => runStep(index + 1), step.hold);
          return;
        }

        this.animateOrbit(
          modelViewer,
          currentTheta,
          currentPhi,
          step.theta,
          step.phi,
          radius,
          step.duration,
          () => {
            currentTheta = step.theta;
            currentPhi = step.phi;
            runStep(index + 1);
          }
        );
      };

      runStep(0);
    } catch (error) {
      finish();
    }
  },
};

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () =>
        this.setupShopifyXR()
      );
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener('DOMContentLoaded', () => { window.ProductModel?.loadShopifyXR(); });
